if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.input = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector(
        '.js-results-container'
      );
      this.btnSubmit = this.querySelector('.js-btn-submit');
      this.btnClearText = this.querySelector('.js-btn-clear-text');
      this.errorMessageContainer = this.querySelector(
        '.js-message-error'
      );
      this.errorMessageText =
        this.errorMessageContainer.querySelector('[data-text]');
      this.timeout = null;
      this.isOpen = false;
      this.drawer = this.closest('.js-drawer');
      this.productsTitle = this.dataset.productsTitle || '';
      this.searchForTemplate =
        this.dataset.searchForTemplate || '__TERMS__';
      this.emptyWithTermsTemplate =
        this.dataset.emptyWithTermsTemplate || '__TERMS__';

      this.input.addEventListener('input', e => {
        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
          const searchTerm = this.input.value.trim();

          if (searchTerm.length <= 0) {
            this.close();

            return;
          }

          this.isOpen = true;

          this.getSearchResults(searchTerm);
        }, 300);
      });

      this.input.addEventListener('keydown', e => {
        const isEscape = e.key === 'Escape';

        if (!isEscape) {
          return;
        }

        if (!this.input.value) {
          if (this.drawer) {
            this.drawer.parentElement.parentElement.toggleDrawer();
          }

          return;
        }

        this.close();
      });

      this.btnClearText.addEventListener('click', e => {
        this.close();
      });
    }

    buildSearchQuery(searchTerm) {
      const terms = searchTerm
        .split(/\s+/)
        .map(term => term.trim())
        .filter(Boolean);

      if (terms.length === 0) {
        return '';
      }

      return terms
        .map((term, index) => {
          const isLastTerm = index === terms.length - 1;
          const normalizedTerm = isLastTerm && !term.endsWith('*')
            ? `${term}*`
            : term;

          return `title:${normalizedTerm}`;
        })
        .join(' ');
    }

    formatTemplate(template, searchTerm) {
      return template.replace('__TERMS__', this.escapeHtml(searchTerm));
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value;

      return div.innerHTML;
    }

    buildResultsMarkup(cardsMarkup, searchTerm) {
      return `
        <div class="predictive-search__results" id="predictive-search-results">
          <div class="predictive-search__row">
            <h6 class="predictive-search__title">${this.productsTitle}</h6>
            <div class="predictive-search__grid">
              ${cardsMarkup}
            </div>
          </div>
          <div class="predictive-search__row">
            <button type="submit" class="link predictive-search__link">
              ${this.formatTemplate(this.searchForTemplate, searchTerm)}
            </button>
          </div>
        </div>
      `;
    }

    buildEmptyMarkup(searchTerm) {
      return `
        <div class="predictive-search__results" id="predictive-search-results">
          <div class="predictive-search__row">
            <div class="predictive-search__message">
              <p>${this.formatTemplate(this.emptyWithTermsTemplate, searchTerm)}</p>
            </div>
          </div>
          <div class="predictive-search__row">
            <button type="submit" class="link predictive-search__link">
              ${this.formatTemplate(this.searchForTemplate, searchTerm)}
            </button>
          </div>
        </div>
      `;
    }

    async getSearchResults(searchTerm) {
      try {
        const scopedSearchQuery = this.buildSearchQuery(searchTerm);

        if (!scopedSearchQuery) {
          this.close();

          return;
        }

        const queryParams = new URLSearchParams({
          q: scopedSearchQuery,
          type: 'product'
        });
        queryParams.append('options[prefix]', 'last');

        const response = await fetch(
          `${Shopify.routes.root}search?${queryParams.toString()}`
        );

        if (!this.isOpen || this.input.value.trim() !== searchTerm) {
          return;
        }

        if (!response.ok) {
          const error = new Error(response.status);

          throw error;
        }

        const text = await response.text();
        const documentFragment = new DOMParser()
          .parseFromString(text, 'text/html');
        const cards = [
          ...documentFragment.querySelectorAll('#product-grid card-product')
        ].slice(0, 6);

        const cardsMarkup = cards
          .map(card => {
            card.classList.add('predictive-search__card-product');
            card.querySelector('.card-product__overlay-content')?.remove();
            card.querySelectorAll('.card-product__sizes').forEach(node => node.remove());
            card.querySelector('.card-product__swatches')?.remove();
            card.querySelector('.product-groups')?.remove();

            return card.outerHTML;
          })
          .join('');

        const resultsMarkup = cardsMarkup
          ? this.buildResultsMarkup(cardsMarkup, searchTerm)
          : this.buildEmptyMarkup(searchTerm);

        this.resultsContainer.innerHTML = resultsMarkup;
        this.open();
      } catch (error) {
        console.error('Live search error:', error);
        this.errorMessageText.textContent = error.message;
        this.errorMessageContainer.classList.remove('hidden');
      }
    }

    open() {
      this.classList.add('is-open');
      this.resultsContainer.classList.remove('hidden');
      this.errorMessageContainer.classList.add('hidden');
      this.input.setAttribute('aria-expanded', 'true');
      this.btnSubmit.setAttribute('tabindex', '-1');
      this.btnClearText.removeAttribute('disabled');
    }

    close() {
      this.isOpen = false;
      this.classList.remove('is-open');
      this.resultsContainer.classList.add('hidden');
      this.errorMessageContainer.classList.add('hidden');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.value = '';
      this.input.focus();
      this.btnSubmit.removeAttribute('tabindex');
      this.btnClearText.setAttribute('disabled', true);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
